import { User } from 'firebase/auth';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { HttpsCallableResult } from 'firebase/functions';
import { getString } from 'firebase/remote-config';

import { CloudFunctionsService } from '@services';
import {
  EngagementData,
  EvaluationName,
  EvaluationResult,
  FirebaseCollections,
  FirebaseDocument,
  GetIpResponse,
  GetPartnerByTokenRequest,
  GetPersonaRequest,
  GetPersonaResponse,
  GetRequiredProgramsRequest,
  Partner,
  Program,
  StatusOption,
} from '@types';
import { rollbar, stripUndefinedValues } from '@utils';
import { store } from 'src/stores';

import { db, remoteConfig, remoteConfigReady } from '../../firebase.config';

// TODO: Write tests for this service.
export class FirebaseService {
  private cloudFunctionsService: CloudFunctionsService;

  constructor(cloudFunctionsService: CloudFunctionsService) {
    this.cloudFunctionsService = cloudFunctionsService;
  }

  public async doesEngagementExist(user: User): Promise<boolean> {
    const lookupEngagement = doc(db, 'engagements', user.uid);

    const engagement = await getDoc(lookupEngagement);

    return engagement.exists();
  }

  public async updateOrCreateFirebaseDocWithId(
    collectionName: FirebaseCollections,
    data: FirebaseDocument
  ): Promise<void> {
    const lookupDocument = doc(db, collectionName, data.id);

    const document = await getDoc(lookupDocument);

    if (document.exists()) {
      const { profile, applicant_trusted_form_url, ...updateData } = data;
      void profile;

      const encryptedTrustedFormUrl = await this.encryptTrustedFormUrl(
        applicant_trusted_form_url
      );
      if (encryptedTrustedFormUrl) {
        updateData.applicant_trusted_form_url = encryptedTrustedFormUrl;
      }

      await updateDoc(lookupDocument, updateData);
    } else {
      let firstName = '';
      if (data.profile) {
        firstName = data.profile.first_name; // Hang onto first name before it gets encrypted
        data.profile = await this.encrypt(data.profile);
      }

      const encryptedTrustedFormUrl = await this.encryptTrustedFormUrl(
        data.applicant_trusted_form_url
      );
      if (encryptedTrustedFormUrl) {
        data.applicant_trusted_form_url = encryptedTrustedFormUrl;
      }

      // Save persona_prequal as blank initially so we can persist the engagement quickly.
      // When enablePersona flag is on, we fill it in the background via fillPersonaPrequalInBackground.
      data.persona_prequal = '';

      // Save the engagement to the database immediately.
      await setDoc(lookupDocument, data);

      // When enabled, kick off persona lookup in the background so we don't block the user.
      const { enablePersona } = store.featureFlagStore.featureFlags;
      if (enablePersona) {
        const personaInputs: GetPersonaRequest = {
          debt_amount: data.attributes.debt_amount,
          household_income: data.attributes.household_income,
          employment_status: data.attributes.employment_status,
          hhi_consistency: data.attributes.hhi_consistency,
          credit_rating: data.attributes.credit_rating,
          payment_status: data.attributes.payment_status,
          first_name: firstName,
        };
        void this.fillPersonaPrequalInBackground(data.id, personaInputs);
      }
    }
  }

  // Calls getPersona and updates the engagement doc with persona_prequal.
  // Fire-and-forget: errors are logged but not thrown.
  private async fillPersonaPrequalInBackground(
    engagementId: string,
    inputs: GetPersonaRequest
  ): Promise<void> {
    try {
      const result: GetPersonaResponse = (
        await this.cloudFunctionsService.getPersona(inputs)
      ).data;
      const engagementRef = doc(db, 'engagements', engagementId);
      await updateDoc(engagementRef, {
        persona_prequal: result.persona_status,
      });
    } catch (error) {
      rollbar.error(
        'Firebase Service: Error filling persona_prequal in background',
        error as Error,
        { engagementId }
      );
    }
  }

  public modifyEngagement(
    engagementData: EngagementData,
    partner: Partner,
    passthroughParams: object
  ): Promise<void> {
    engagementData.date_created = serverTimestamp();
    engagementData.partner_mode = partner.mode;
    engagementData.attributes.forceQualification =
      partner.forceQualification || false;
    // Firestore rejects `undefined` field values outright, so guard against a
    // partner host handing us a passthrough key that was never filled in.
    const { value: cleanedPassthroughParams, dropped } = stripUndefinedValues(
      passthroughParams || {}
    );
    if (dropped.length) {
      rollbar.info(
        'Firebase Service: Dropped undefined passthroughParams before Firestore write',
        { engagementId: engagementData.id, droppedParams: dropped }
      );
    }
    engagementData.passthroughParams = cleanedPassthroughParams;

    return this.updateOrCreateFirebaseDocWithId('engagements', engagementData);
  }

  public postUserTimeout(data: User): Promise<void> {
    const timeoutData = {
      id: data.uid,
      timed_out: true,
    };

    return this.updateOrCreateFirebaseDocWithId('engagements', timeoutData);
  }

  public async updateEngagementStatus(
    user: User,
    status: StatusOption
  ): Promise<void> {
    const statusData = {
      id: user.uid,
      status,
    };

    return this.updateOrCreateFirebaseDocWithId('engagements', statusData);
  }

  public async updateEngagementLeadType(
    user: User,
    leadType: EvaluationName
  ): Promise<void> {
    return this.updateOrCreateFirebaseDocWithId('engagements', {
      id: user.uid,
      lead_type: leadType,
    });
  }

  public async getFirebaseRemoteConfig(configName: string) {
    await remoteConfigReady;
    // Boot activates once; reads use Firebase's in-memory cache so a closed
    // IndexedDB connection cannot break configuration reads mid-session.
    return JSON.parse(getString(remoteConfig, configName));
  }

  /**
   * Sends an evaluation request to ida-api via Firebase callable function.
   * The function handles JWT authentication server-side.
   */
  public async sendEvaluation(
    data: User,
    language: string = 'en'
  ): Promise<EvaluationResult[]> {
    try {
      const { data: results } = await this.cloudFunctionsService.sendEvaluation(
        {
          userId: data.uid,
          language,
        }
      );
      return results;
    } catch (error) {
      rollbar.error(
        'Firebase Service: Error sending evaluation',
        error as Error
      );
      throw new Error(`Error sending evaluation: ${error}`);
    }
  }

  public async getPartnerByToken(
    request: GetPartnerByTokenRequest
  ): Promise<HttpsCallableResult<Partner>> {
    try {
      return this.cloudFunctionsService.getPartnerByToken(request);
    } catch (error) {
      throw new Error(`Error retrieving partner by token: ${error}`);
    }
  }

  public async getRequiredPrograms(
    request: GetRequiredProgramsRequest
  ): Promise<HttpsCallableResult<Program[]>> {
    return this.cloudFunctionsService.getPrograms(request);
  }

  public async blacklistEvent() {
    return this.cloudFunctionsService.blacklistEvent();
  }

  public async getUserIp(): Promise<HttpsCallableResult<GetIpResponse>> {
    try {
      return this.cloudFunctionsService.getUserIp();
    } catch (error) {
      rollbar.error(
        'Firebase Service: Error retrieving user IP',
        error as Error
      );
      throw new Error(`Error retrieving user IP: ${error}`);
    }
  }

  private prepareEncryptionData(
    data: { [key: string]: string },
    fields: string[]
  ) {
    const sanitizedData: { [key: string]: string } = {};

    for (const field of fields) {
      if (data[field]) {
        sanitizedData[field] = data[field];
      }
    }

    return { sanitizedData, finalData: { ...data, ...sanitizedData } };
  }

  /**
   * Encrypts the applicant_trusted_form_url if provided.
   * @param trustedFormUrl - The trusted form URL to encrypt, or undefined/null if not provided.
   * @returns The encrypted trusted form URL, or undefined if no URL was provided.
   */
  private async encryptTrustedFormUrl(
    trustedFormUrl?: string
  ): Promise<string | undefined> {
    if (!trustedFormUrl) {
      return undefined;
    }

    const { data: encryptedData } =
      await this.cloudFunctionsService.postEncrypt({
        applicant_trusted_form_url: trustedFormUrl,
      });

    return encryptedData.applicant_trusted_form_url;
  }

  public async encrypt(data: { [key: string]: string }) {
    const fields = ['first_name', 'last_name', 'email', 'phone_mobile'];

    const { sanitizedData, finalData } = this.prepareEncryptionData(
      data,
      fields
    );

    const { data: encryptedData } =
      await this.cloudFunctionsService.postEncrypt(sanitizedData);

    return { ...finalData, ...encryptedData };
  }
}
